import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ArrowForwardIcon, Button, Paper, Stack, Typography, Box } from '@esgian/esgianui';
import { DISPLAY_UNIT, SUB_SECTIONS } from '@constants/index';
import { useUpdateSection, useDisplayUnit, useSeriesBuilder, useTheme } from '@hooks';
import { OperationsPeriodicOverviewChart } from '@components/Charts';
import { useSelector } from 'react-redux';
import { getDisplayUnit } from '@store/features';
import TextWithTooltipIcon from '@components/Display/TextWithTooltipIcon';
import { getUnixTimeStamp } from '@helpers';
import { findOverviewSignal } from '@api/helpers';

function OperationsPeriodicOverview({ loading, overviewData }) {
  const { updateMainPageSection } = useUpdateSection();
  const { getUnitTitle } = useDisplayUnit();
  const { getSeriesData } = useSeriesBuilder();
  const displayUnit = useSelector(getDisplayUnit);
  const { theme } = useTheme();
  const {
    palette: {
      charts: { colorsSameType }
    }
  } = theme;
  const { categories, series, title } = useMemo(() => {
    if (!overviewData?.data) return {};
    
    const signals = overviewData.data;
    
    // Find signals based on display unit
    const accomSignal = findOverviewSignal(signals, 'Accom', displayUnit);
    const drillingSignal = findOverviewSignal(signals, 'Drilling', displayUnit);
    const propulsionSignal = findOverviewSignal(signals, 'Propulsion', displayUnit);
    
    const accommodation = accomSignal?.timeSeriesValues ?? [];
    const drilling = drillingSignal?.timeSeriesValues ?? [];
    const propulsion = propulsionSignal?.timeSeriesValues ?? [];
    let series = [];

    const {
      max: maxAccom,
      defaultArray: defaultAccom,
      convertedArray: convertedAccom
    } = getSeriesData(accommodation, displayUnit);
    const {
      max: maxDrilling,
      defaultArray: defaultDrilling,
      convertedArray: convertedDrilling
    } = getSeriesData(drilling, displayUnit);
    const {
      max: maxPropulsion,
      defaultArray: defaultPropulsion,
      convertedArray: convertedPropulsion
    } = getSeriesData(propulsion, displayUnit);

    let max = Math.max(maxAccom, maxDrilling, maxPropulsion);
    const { title, converted } = getUnitTitle(max);
    // Each signal must use its own timestamps - partially instrumented rigs
    // can have an empty Accom series while Drilling still has data.
    const toDates = (timeSeries) => Object.keys(timeSeries).map((key) => getUnixTimeStamp(key));
    const accomDates = toDates(accommodation);
    const drillingDates = toDates(drilling);
    const propulsionDates = toDates(propulsion);

    if (defaultAccom.length) {
      series.push({
          label: 'Hotel and utilities',
          type: 'bar',
          borderColor: colorsSameType[0],
          backgroundColor: colorsSameType[0],
          borderSkipped: true,
          stack: 1,
          data: [...(converted ? convertedAccom : defaultAccom)].map((val, i) => {
            return { x: accomDates[i], y: val };
          })
        });
    }

    if (defaultDrilling.length) {
      series.push({
          label: 'Drilling equipment',
          type: 'bar',
          stack: 1,
          borderColor: colorsSameType[1],
          backgroundColor: colorsSameType[1],
          borderSkipped: true,
          data: [...(converted ? convertedDrilling : defaultDrilling)].map((val, i) => {
            return { x: drillingDates[i], y: val };
          })
        });
    }

    if (defaultPropulsion.length) {
      series.push(
        {
          label: 'Propulsion equipment',
          type: 'bar',
          stack: 1,
          borderColor: colorsSameType[2],
          backgroundColor: colorsSameType[2],
          borderSkipped: true,
          data: [...(converted ? convertedPropulsion : defaultPropulsion)].map((val, i) => {
            return { x: propulsionDates[i], y: val };
          })
        });
    }
    return {
      title: title,
      series: series
    };
  }, [overviewData, displayUnit, theme]);

  const getSubTitle = useMemo(() => {
    switch (displayUnit) {
      case DISPLAY_UNIT.CO2:
        return 'Daily CO2e emissions in period';
      case DISPLAY_UNIT.NOX:
        return 'Daily NOx emissions in period';
      case DISPLAY_UNIT.FUEL:
        return 'Daily fuel consumption in period';
      case DISPLAY_UNIT.ENERGY:
        return 'Daily energy consumption in period';
    }
  }, [displayUnit]);

  return (
    <Paper sx={{ p: 2, minHeight: '100%' }}>
      <Stack spacing={2}>
        <Box>
          <TextWithTooltipIcon
            tooltipText={
              'Total consumption or emissions in the selected time period for each system category shown per day.'
            }
            label={<Typography variant={'h6'}>System overview</Typography>}
          />
          <Typography variant="body2">{getSubTitle}</Typography>
        </Box>
        <OperationsPeriodicOverviewChart
          unitTitle={title}
          loading={loading}
          categories={categories}
          series={series}
        />
        <Stack direction={'row'} justifyContent={'flex-end'}>
          <Button
            onClick={() => updateMainPageSection(SUB_SECTIONS.DRILLING_ACTIVITIES)}
            endIcon={<ArrowForwardIcon />}
            variant={'outlined'}>
            See Drilling activities page
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

OperationsPeriodicOverview.propTypes = {
  loading: PropTypes.bool,
  overviewData: PropTypes.object
};

OperationsPeriodicOverview.defaultProps = {
  overviewData: null,
  loading: false
};

export default OperationsPeriodicOverview;
